import { AfterViewInit, Component, HostBinding, HostListener, OnDestroy, ViewChild, ViewEncapsulation, ElementRef } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { Endpoint, HelpIndex, MessageType, TemplateOperationType, UpdateStatusType, SettingsType } from '../../../models/enums';
import { ExportMetadataProfile } from '../../../models/export-metadata-profile';
import { LoadFileProjectTemplate } from '../../../models/load-file-project-template';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../models/permission';
import { Project } from '../../../models/project';
import { Role } from '../../../models/role';
import { SettingsNavigationContentComponent } from '../../../models/settings-navigation-content-component';
import { Template, TemplateType } from '../../../models/template';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { GenericTreeComponent } from '../../generic-tree/generic-tree.component';
import { CreateEditEntityModalComponent } from '../create-edit-entity-modal/create-edit-entity-modal.component';
import { DeleteEntityModalComponent } from '../delete-entity-modal/delete-entity-modal.component';
import { OrganizationMenu, ReviewMenu } from './organization-menu';
import { ProjectMenu } from './project-menu';
import { SaveAsLoadFromTemplateComponent } from './save-as-load-from-template/save-as-load-from-template.component';
import { MetadataFieldsComponent } from './metadata-fields/metadata-fields.component';
import { SystemMenu } from './system-menu';
import { ContextMenuComponent } from '../../context-menu/context-menu.component';
import * as _ from 'underscore';
import { MetadataService } from '../../../services/metadata/metadata.service';
import { ProjectListItem } from '../../../models/project-list-item';

@Component({
    standalone: false,
    selector: 'res-settings',
    templateUrl: './settings-modal.component.html',
    styleUrls: ['./settings-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class SettingsModalComponent extends BaseModalComponent implements AfterViewInit, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('settingsTree', { static: true }) settingsTree: GenericTreeComponent;
    @ViewChild('selectedSettingTree') selectedSettingTree: GenericTreeComponent;
    @ViewChild(RouterOutlet) outlet: RouterOutlet;
    @ViewChild('loadXmlLabelSingle') loadXmlLabelSingle: ElementRef;
    @ViewChild('loadXmlLabelMulti') loadXmlLabelMulti: ElementRef;
    selectedNodeTemplateType: TemplateType;
    selectedNodeTemplateHandle: 0;
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;
    @HostListener('window:keydown.esc', ['$event'])
    onEsc(event: KeyboardEvent): void {

        const modals = (event.target as any).getElementsByClassName('modal-content');
        let isSettingsModal = modals.length === 1 && modals[0].firstElementChild?.tagName === 'RES-SETTINGS';
        if (!isSettingsModal && (event.target as any).getElementsByClassName('modal-content').length === 0) {
            const modalIndex = _.findIndex((event as any).path, elem => elem.tagName === 'RES-BASE-MODAL');
            isSettingsModal = (event as any).path[modalIndex + 1].tagName === 'RES-SETTINGS';
        }

        if (isSettingsModal) {
            this.closeModal();
        }
    }

    modalAutomationId = 'settings-popup';
    modalClass = 'settings-modal';
    modalHelpIndex: HelpIndex;

    settingsType: SettingsType;
    hideCaseSettings: boolean;
    hideOrgSettings: boolean;
    showSystemSettings: boolean;
    currentProject: Project;
    currentOrganization: string;

    nodesProject = ProjectMenu;
    nodesOrg = OrganizationMenu;
    nodesSystem = SystemMenu;
    selectedSettingNodes: Array<GenericTreeNode>;

    itemListIsBusy: boolean;
    itemListIsBusyMessage: string;

    settingsInfoByType = new Map<SettingsType, any>();
    selectedSetting: GenericTreeNode;
    selectedSettingChild: GenericTreeNode;
    currentNavigationComponent: SettingsNavigationContentComponent;
    private entityChangedEvent: Subscription;
    isSystemSettingsModalOpen = false;

    constructor(
        private modalService: BsModalService,
        private settingsModalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private router: Router,
        private contextMenuComponent: ContextMenuComponent,
        private metadataService: MetadataService) {

        super(modalService, settingsModalRef);
        if (this.commonService.isReview) {
            this.nodesOrg = this.nodesOrg.concat(ReviewMenu);
        }
        this.nodesOrg.forEach(n => n.filterBy = node => !node.permissions || (node.permissions && Permissions.checkUserPermissions(node.permissions)));
        this.nodesSystem = _.clone(SystemMenu);
        this.nodesSystem.forEach(c => c.filterBy = node => !node.permissions || (node.permissions && Permissions.checkUserPermissions(node.permissions)));
        const tempNodes = _.clone(this.nodesSystem);
        tempNodes.forEach(
            (n, id) => {
                if (n?.children?.length > 0) {
                    let nodeParentVisible = false;
                    n.children.forEach(c => {
                        if (!c.permissions || (c.permissions && Permissions.checkUserPermissions(c.permissions))) {
                            nodeParentVisible = true;
                        }
                    });
                    if (!nodeParentVisible) {
                        const idxToDelete = this.nodesSystem.findIndex(t => t.name === n.name);
                        if (idxToDelete > -1) {
                            this.nodesSystem.splice(idxToDelete, 1);
                        }
                    }
                }
            });
        this.nodesProject.forEach(
            n => {
                n.filterBy = node => !node.permissions || (node.permissions && Permissions.checkUserPermissions(node.permissions));
                if (n && n.name === 'eDiscovery' && n.children && n.children.length > 0) {
                    n.children.forEach(c => {
                        // save to/load from context menu button show/hide based on permissions and options visibility
                        if (c.permissions && c.permissions.length > 0) {
                            c.showContextMenuButton = Permissions.checkUserPermission(c.permissions[0].permissionType);
                            if (c.contextMenuOptions) {
                                const visibleOptions = c.contextMenuOptions.filter(o => o.isVisible(c));
                                c.showContextMenuButton = c.showContextMenuButton && visibleOptions && visibleOptions.length > 0;
                                // special case for alias lists who don't have system templates, so hide context menu if it's visible only because has sys templates permissions
                                if (c.permissions[0].permissionType === ObjectActionListType.PRJ_ALIAS_LISTS && visibleOptions.length === 1 &&
                                    !Permissions.checkUserPermission(ObjectActionListType.ORG_ALIAS_LISTS)) {
                                    c.showContextMenuButton = false;
                                }
                            }
                        }
                    });
                }
            }
        );
        const tlsOrgPermissions = [
            new PermissionSet({ permissionType: ObjectActionListType.REEF_EXPRESS_PREFERENCES, permissionActions: [ActionType.VIEW] }),
            new PermissionSet({ permissionType: ObjectActionListType.TLS_SALES_EMAIL_RECIPIENTS, permissionActions: [ActionType.VIEW] }),
            new PermissionSet({ permissionType: ObjectActionListType.TLS_PM_EMAIL_RECIPIENTS, permissionActions: [ActionType.VIEW] })
        ];
        if (this.commonService.tlsExpressSystem) {
            if (!Permissions.checkUserPermission(ObjectActionListType.TLS_PROJECT_CONFIG)) {
                this.nodesProject = this.nodesProject.filter(n => n.name !== 'Reef Express');
            }
            if (!Permissions.checkUserPermissions(tlsOrgPermissions, true)) {
                this.nodesOrg = this.nodesOrg.filter(n => n.name !== 'Reef Express');
            }
        } else {
            this.nodesProject = this.nodesProject.filter(n => n.name !== 'Reef Express');
            this.nodesOrg = this.nodesOrg.filter(n => n.name !== 'Reef Express');
        }
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data === UpdateStatusType.TEMPLATE_MEMEBERSHIP) {
                this.loadNodeListItems(this.selectedSetting);
            } else if (data && data.updateStatusType && data.updateStatusType === UpdateStatusType.TEMPLATE_MEMEBERSHIP
                && data.autoSelectHandle) {
                this.loadNodeListItems(this.selectedSetting, data.autoSelectHandle);
            } else if (Object.values(TemplateType).includes(data)
                && (this.selectedSettingChild && this.selectedSettingChild.data ?
                    (this.selectedSettingChild.data.templateType === data || this.selectedSettingChild.bundle === data) : this.selectedSetting?.data?.templateType === data)) {
                // a load from template operation was done => reload data for current component
                this.currentNavigationComponent.selectedTreeItemChanged(this.selectedSettingChild ? this.selectedSettingChild : this.selectedSetting,
                    this.settingsType === SettingsType.project ? this.currentProject.handle : null, this.settingsType);
                this.loadNodeListItems(this.selectedSetting);
            }
        });
        this.commonService.getMessage(MessageType.fieldsUpdated).subscribe((data) => {
            if (this.settingsType === SettingsType.project) {
                this.selectedSettingChild.data.entries = data?.data?.profile?.entries;
            }
            this.loadNodeListItems(this.selectedSetting);
        });
        this.settingsInfoByType.set(SettingsType.organization, { nodes: this.nodesOrg, helpIdx: HelpIndex.organization_mgmt_overview });
        this.settingsInfoByType.set(SettingsType.project, { nodes: this.nodesProject, helpIdx: HelpIndex.admin_manage_settings_dlg });
        this.settingsInfoByType.set(SettingsType.system, { nodes: this.nodesSystem, helpIdx: HelpIndex.systemsettingsoverview });
    }

    ngAfterViewInit(): void {
        this.currentProject = this.commonService.currentProject || (this.modalData instanceof Project && this.modalData.handle ? this.modalData : null);
        // this condition is to hide/show Org/Prj settings when the logged in user is a system user
        const hasPrjSettingsPermissions = this.modalData && this.modalData.parent && this.modalData.parent.hasPrjSettingsPermissions ?
            this.modalData.parent.hasPrjSettingsPermissions : this.modalData && this.modalData.hasPrjSettingsPermissions ?
                this.modalData.hasPrjSettingsPermissions : Permissions.hasPrjSettingsPermissions;
        const hasOrgSettingsPermissions = this.modalData && this.modalData.parent && this.modalData.parent.hasOrgSettingsPermissions ?
            this.modalData.parent.hasOrgSettingsPermissions : this.modalData && this.modalData.hasOrgSettingsPermissions ?
                this.modalData.hasOrgSettingsPermissions : Permissions.hasOrgSettingsPermissions;
        const hasSysSettingsPermissions = this.modalData && this.modalData.parent && this.modalData.parent.hasSysSettingsPermissions ?
            this.modalData.parent.hasSysSettingsPermissions : this.modalData && this.modalData.hasSysSettingsPermissions ?
                this.modalData.hasSysSettingsPermissions : Permissions.hasSysSettingsPermissions;
        this.hideOrgSettings = !hasOrgSettingsPermissions || Boolean(this.modalData.isOrgSettingsHidden);
        this.hideCaseSettings = !this.currentProject || !hasPrjSettingsPermissions;
        const isSuperSystemUser = this.commonService.currentUser?.originalCustomerName === 'super_system_customer';
        this.showSystemSettings = !this.modalData.isSystemSettingsHidden && hasSysSettingsPermissions && (isSuperSystemUser || this.commonService.currentUser?.importedFromSystemUser);
        this.currentOrganization = this.commonService.currentUser?.originalCustomerName === 'super_system_customer' && this.settingsType === SettingsType.system ? 'super_system_customer' :
            this.commonService?.currentUser?.customerName || this.modalData?.organizationName || this.modalData?.orgName;
        // add the project handle to all child nodes of Ediscovery tree node in order to be used in saveTo/LoadFrom template dialog
        if (this.currentProject) {
            const treeNodeIndex = this.commonService.tlsExpressSystem && this.nodesProject?.length === 3 ? 2 : 1;
            this.nodesProject[treeNodeIndex]?.children?.forEach(n => {
                if (n.data) {
                    n.data.handle = this.currentProject.handle;
                }
            });
        }
        if (!this.modalData.isSystemSettingsHidden && this.showSystemSettings) {
            this.restService.post(Endpoint.realmManager_initializeOrganization, {
                organizationName:
                    this.commonService.currentUser?.originalCustomerName === 'super_system_customer' && this.settingsType === SettingsType.system ? 'super_system_customer' :
                        this.commonService?.currentUser?.customerName || this.modalData?.organizationName || this.modalData?.orgName,
                systemScope: this.isSystemSettingsModalOpen
            });

            this.setSettingsType(this.modalData.openType ? SettingsType[this.modalData.openType] : SettingsType.system);
        } else {
            if (this.modalData.openType) {
                this.setSettingsType(SettingsType[this.modalData.openType]);
            } else {
                this.setSettingsType(this.hideCaseSettings ? SettingsType.organization : SettingsType.project);
            }
        }

        this.isSystemSettingsModalOpen = this.settingsType === SettingsType.system && this.showSystemSettings;
        setTimeout(() => {
            // if setting type is available, select the desired setting
            if (this.modalData && this.modalData.setting && this.modalData.setting.length > 0) {
                const node = this.settingsTree.getNodeBy(n => n.name === this.modalData.setting);
                if (node) {
                    this.settingsTree.selectNode(node);
                    this.setSelectedNode(node, false);
                }
            }
        });
    }

    preselectSettingCallback = (node: GenericTreeNode): void => {
        this.checkCurrentNavigationComponent().then(result => {
            if (result) {
                this.setSelectedNode(node, false);
            }
        });
    }

    preselectSettingChildCallback = (node: GenericTreeNode): void => {
        this.checkCurrentNavigationComponent().then(result => {
            if (result) {
                this.setSelectedNode(node, true);
            }
        });
    }

    checkSelectedNode(node: GenericTreeNode): void {
        if (node) {
            this.checkCurrentNavigationComponent().then(result => {
                if (result) {
                    this.setSelectedNode(node, true);
                }
            });
        }
    }

    setSelectedNode(node: GenericTreeNode, isListItem: boolean, skipNavigation = false): void {
        if (node) {
            if (isListItem) {
                this.selectedSettingChild = node;
                this.selectedSettingTree?.selectNode(node);
            } else {
                this.selectedSetting = node;
                this.selectedSettingChild = null;
                this.settingsTree.selectNode(node);
            }
            if (!skipNavigation) {
                if (node.navigationInfo && node.navigationInfo.tabs.length > 0) {
                    if ((this.currentNavigationComponent instanceof MetadataFieldsComponent && this.currentNavigationComponent.type === 'DOCUMENT_METADATA' && node.name === 'Metadata Search Fields') ||
                        (this.currentNavigationComponent instanceof MetadataFieldsComponent && this.currentNavigationComponent.type === 'SEARCH_FIELDS' && node.name === 'Metadata View Fields')) {
                        this.navigateTo('settings-empty');
                        setTimeout(() => {
                            this.navigateTo(node.navigationInfo.tabs.filter(t => t.default)[0].path);
                        });
                    } else {
                        this.navigateTo(node.navigationInfo.tabs.filter(t => t.default)[0].path);
                    }
                } else if (node.requestInfo) {
                    // clear content until item list loaded
                    this.navigateTo('settings-empty');
                    this.loadNodeListItems(node);
                } else {
                    // not yet implemented
                    this.navigateTo('settings-empty');
                }
            }
        }
    }

    navigateTo(path: string): void {
        if (path) {
            if (path === 'settings-empty') {
                this.currentNavigationComponent = null;
            }
            if (this.router.url.includes(`(settings-router-outlet:${path})`) && this.currentNavigationComponent && this.currentNavigationComponent.selectedTreeItemChanged) {
                this.onRouterOutletActivate(this.currentNavigationComponent);
            } else {
                this.clearRouteOutletAndNavigate(path);
            }
        }
    }

    clearRouteOutletAndNavigate(path: string): void {
        if (this.outlet) {
            if (this.outlet.isActivated) {
                const routerOutletSubscribtion = this.outlet.deactivateEvents.subscribe(() => {
                    routerOutletSubscribtion.unsubscribe();
                    this.router.navigate([{ outlets: { 'settings-router-outlet': path ? [path] : null } }], { skipLocationChange: true });
                });
                this.outlet.deactivate();
            } else {
                this.router.navigate([{ outlets: { 'settings-router-outlet': path ? [path] : null } }], { skipLocationChange: true });
            }
        }
    }

    loadNodeListItems(node: GenericTreeNode, autoSelectHandle?: string): void {
        if (node && node.requestInfo) {
            const isTemplateNode = (node.requestInfo.responseName.indexOf('templates') !== -1) ||
                (node.requestInfo.responseName.indexOf('profiles') !== -1);
            const crtPermissionType = node.permissions && node.permissions.length === 1 ? node.permissions[0].permissionType : null;
            let hasCreatePermissions = true;
            if (crtPermissionType) {
                hasCreatePermissions = Permissions.checkUserPermission(crtPermissionType, ActionType.CREATE);
            }
            this.selectedSettingNodes = [new GenericTreeNode({
                name: isTemplateNode ? (node?.name === 'Template Profiles' ? 'Template Profiles' : 'Templates') : (node?.name?.includes('Role Permissions') ? 'Roles' : node?.name),
                automationId: (node.automationId ? node.automationId : 'selected-setting') + '-tree',
                isExpandable: true,
                isExpanded: true,
                isSelectable: false,
                hideExpander: true,
                createChild: node.createChild,
                requestInfo: node.requestInfo,
                showHeaderButton: hasCreatePermissions,
                headerButtons: node.headerButtons,
                contextMenuOptions: node.contextMenuOptions,
                showContextMenuButton: node.showContextMenuButton,
                permissions: node.permissions
            })];
            if (this.selectedSettingTree) {
                this.selectedSettingTree.setData(this.selectedSettingNodes);
            }

            this.itemListIsBusy = true;
            this.itemListIsBusyMessage = 'Loading...';
            if (this.settingsType === SettingsType.project) {
                node.requestInfo.data = { handle: this.currentProject.handle };
            }
            if (isTemplateNode && this.settingsType === SettingsType.organization) {
                Object.assign(node.requestInfo.data, { organizationName: this.currentOrganization });
            }
            if (this.isSystemSettingsModalOpen) {
                Object.assign(node.requestInfo.data, { systemScope: true });
            }
            this.restService.post(node.requestInfo.endpoint, node.requestInfo.data).then(response => {
                if (response && this.selectedSettingTree) {
                    this.selectedSettingTree.setNodeData(this.selectedSettingNodes[0], response);
                    if (this.selectedSettingNodes[0].children.length > 0) {
                        let selNode;
                        let skipNavigation: boolean;
                        if (autoSelectHandle) {
                            selNode = this.selectedSettingChild && this.selectedSettingChild.bundle === TemplateType.LOADFILE_SETTINGS ? this.selectedSettingTree.getNodeByField('id', autoSelectHandle) :
                                this.selectedSettingTree.getNodeByHandle(autoSelectHandle);
                            autoSelectHandle = null;
                        } else {
                            // select previous item or default...if it still exists
                            if (this.selectedSettingChild && this.selectedSettingChild.data) {
                                if (this.selectedSettingChild.data.handle ||
                                    this.selectedSettingChild.bundle === TemplateType.EXPORT_FIELDS) {
                                    selNode = this.selectedSettingTree.getNodeByHandle(this.selectedSettingChild.data.handle);
                                    if (selNode && selNode.data && selNode.data.handle === this.selectedSettingChild.data.handle) {
                                        skipNavigation = true;
                                    }
                                    // special case for load file import due to use of id instead of handle
                                } else if (this.selectedSettingChild.bundle === TemplateType.LOADFILE_SETTINGS && this.selectedSettingChild.data.id) {
                                    selNode = this.selectedSettingTree.getNodeByField('id', this.selectedSettingChild.data.id);
                                }
                            }
                            if (!selNode) {
                                selNode = this.selectedSettingTree.getNodeBy((item) => item.customText && item.customText === 'Default');
                            }
                        }
                        if (selNode) {
                            this.setSelectedNode(selNode, true, skipNavigation);
                        } else {
                            this.setSelectedNode(this.selectedSettingNodes[0].children[0], true);
                        }
                    }
                }
                this.itemListIsBusy = false;
            }, error => {
                this.itemListIsBusy = false;
            });
        }
    }

    createNewTemplate(node: GenericTreeNode): void {
        let modalData;
        if (node.requestInfo.endpoint === Endpoint.projectManager_listLoadFileMetadataProfiles) {
            modalData = new LoadFileProjectTemplate({ projectId: node.requestInfo.data.handle });
        } else if (node.requestInfo.endpoint === Endpoint.projectManager_listExportMetadataProfile) {
            modalData = new ExportMetadataProfile({ projectId: node.requestInfo.data.handle });
        } else {
            modalData = new Template({ templateType: node.requestInfo.data.tempType, handle: 'autoSelect', orgId: this.currentOrganization });
        }
        if (this.settingsType === SettingsType.system) {
            modalData.scope = 'SYSTEM_LEVEL';
            modalData.isSystemSettingsContext = this.isSystemSettingsModalOpen;
        }
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: modalData });
    }

    onContextMenuActionCallback = (node: GenericTreeNode, action: string): void => {
        if (node && action) {
            const isSystemRoleApi = node?.requestInfo?.endpoint === Endpoint.realmManager_listSystemRoles;
            switch (action) {
                case 'New Template ...': {
                    this.contextMenuComponent.closeAllContextMenus();
                    this.checkCurrentNavigationComponent().then(result => {
                        if (result) {
                            this.createNewTemplate(node);
                        }
                    });
                    break;
                }
                case 'Download All as XMLs ...': {
                    let allHandles = node?.children?.map(n => n.data?.handle);
                    if (node.children?.length === 0 && this.selectedSettingNodes?.length > 0) {
                        const selectedNodeTemplates = this.selectedSettingNodes[0].children;
                        allHandles = selectedNodeTemplates?.map(n => n.data.handle);
                    }
                    // Show a confirmation dialog if the template has changes that aren't saved yet
                    if (this.currentNavigationComponent?.isDirty) {
                        this.commonService.openConfirmationModal(this.selectedSettingChild.name + ' contains unsaved changes.', 'Confirmation', 'Save Changes and Download XML', 'Cancel').then((result) => {
                            if (result && result.modalResult) {
                                const saveChanges = this.currentNavigationComponent.saveChanges();
                                if (saveChanges) {
                                    this.exportAllTemplates(allHandles, node);
                                }
                            }
                        });
                    } else {
                        this.exportAllTemplates(allHandles, node);
                    }
                    break;
                }
                case 'Load from XMLs ...': {
                    this.selectedNodeTemplateType = node.bundle ? node.bundle : node.requestInfo.data.tempType;

                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.accept = '.xml,.zip';

                    input.onchange = (event: Event) => {
                        const files = (event.target as HTMLInputElement)?.files;
                        if (!files?.length) return;

                        const templateData: any[] = [];
                        let filesProcessed = 0;

                        const processFile = (file: File) => {
                            const fileName = file.name.toLowerCase();
                            const isZip = fileName.endsWith('.zip');
                            const isXml = fileName.endsWith('.xml');

                            if (!isZip && !isXml) {
                                checkAllFilesProcessed();
                                return;
                            }

                            const reader = new FileReader();
                            reader.onload = (e: any) => {
                                let binaryString = '';

                                if (this.commonService.currentBrowserIsIE) {
                                    binaryString = this.commonService.convertArrayToBinaryString(reader.result);
                                } else {
                                    binaryString = e.target.result;
                                }

                                const encoded = btoa(binaryString);

                                templateData.push({
                                    uploadedTemplateData: encoded,
                                    compressed: isZip
                                });

                                checkAllFilesProcessed();
                            };

                            if (this.commonService.currentBrowserIsIE) {
                                reader.readAsArrayBuffer(file);
                            } else {
                                reader.readAsBinaryString(file);
                            }
                        };

                        const checkAllFilesProcessed = () => {
                            filesProcessed++;
                            if (filesProcessed === files.length) {
                                const payload = {
                                    templateData: templateData,
                                    templateType: this.selectedNodeTemplateType,
                                    projectHandle: this.settingsType === SettingsType.project ? this.currentProject?.handle : null
                                };

                                this.restService.post(Endpoint.templateManager_importTemplates, payload).then(response => {
                                    const errorMessage = response?.messages?.length > 0 ? response.messages[0] : ''
                                    if (errorMessage.length > 0) {
                                        this.commonService.openErrorModal(errorMessage);
                                    } else {
                                        this.loadNodeListItems(this.selectedSetting);
                                    }
                                });
                            }
                        };

                        for (let i = 0; i < files.length; i++) {
                            processFile(files[i]);
                        }
                    };

                    input.click(); // Open file picker
                    break;
                }
                case 'Download as XML ...': {
                    const handle = node?.data?.handle ? node.data.handle : '';
                    // Show a confirmation dialog if the template has changes that aren't saved yet
                    if (this.currentNavigationComponent.isDirty) {
                        this.commonService.openConfirmationModal('This template contains unsaved changes.', 'Confirmation', 'Save Changes and Download XML', 'Cancel').then((result) => {
                            if (result && result.modalResult) {
                                const saveChanges = this.currentNavigationComponent.saveChanges();
                                if (saveChanges) {
                                    this.exportTemplates(handle, node);
                                }
                            }
                        });
                    } else {
                        this.exportTemplates(handle, node);
                    }

                    break;
                }
                case 'Load from XML ...': {
                    this.selectedNodeTemplateType = node.bundle ? node.bundle : node.data?.templateType ? node.data.templateType : '';
                    this.selectedNodeTemplateHandle = node?.data?.handle ? node.data.handle : '';

                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.accept = '.xml'; // optional
                    fileInput.multiple = false;

                    fileInput.addEventListener('change', (event: any) => {
                        const file = event?.target?.files[0];
                        const reader: any = new FileReader();

                        reader.onload = (readerEvt): any => {
                            let binaryString = '';

                            if (this.commonService.currentBrowserIsIE) {
                                binaryString = this.commonService.convertArrayToBinaryString(reader.result);
                                reader.content = binaryString;
                            } else {
                                binaryString = readerEvt.target.result;
                            }

                            binaryString = btoa(binaryString);

                            const request = [{
                                uploadedTemplateData: binaryString,
                                compressed: false,
                                templateHandle: this.selectedNodeTemplateHandle
                            }];

                            this.restService.post(
                                Endpoint.templateManager_importTemplates,
                                { templateData: request, templatFeType: this.selectedNodeTemplateType, projectHandle: this.settingsType === SettingsType.project ? this.currentProject?.handle : null }
                            ).then(response => {
                                const errorMessage = response?.messages?.length > 0 ? response.messages[0] : ''
                                if (errorMessage.length > 0) {
                                    this.commonService.openErrorModal(errorMessage);
                                } else {
                                    this.loadNodeListItems(this.selectedSetting);
                                    this.setSelectedNode(node, true);
                                }
                            });
                        };

                        if (this.commonService.currentBrowserIsIE) {
                            reader.readAsArrayBuffer(file);
                        } else {
                            reader.readAsBinaryString(file);
                        }
                    });

                    // Trigger the file input
                    fileInput.click();
                    break;
                }
                case 'New Role ...': {
                    let modalData;
                    if (node.requestInfo.endpoint === Endpoint.orgManager_listRoles || isSystemRoleApi) {

                        modalData = new Role({ name: node.requestInfo.data.name, handle: 'autoSelect', isSystemRole: isSystemRoleApi, settingsType: this.settingsType });
                    }
                    this.commonService.openModal(CreateEditEntityModalComponent, { modalData: modalData });
                }
                    break;
                case 'Edit ...': {
                    node.data.isSystemRole = this.isSystemSettingsModalOpen;
                    this.commonService.openModal(CreateEditEntityModalComponent, { modalData: node.data });
                    break;
                }
                case 'Delete': {
                    node.data.isSystemRole = this.isSystemSettingsModalOpen;
                    this.commonService.openModal(DeleteEntityModalComponent, { modalData: node.data }).then((modalResponse) => {
                        if (modalResponse.modalResult && this.settingsType === SettingsType.system) {
                            if (this.selectedSettingNodes && this.selectedSettingNodes[0]?.children?.length === 1) {
                                this.navigateTo('settings-empty');
                            }
                        }
                    });
                    break;
                }
                case 'Copy ...': {
                    node.data.settingsType = this.settingsType;
                    node.data.isSystemRole = this.isSystemSettingsModalOpen;
                    this.commonService.openModal(CreateEditEntityModalComponent, { modalData: node.data, options: { copy: true } });
                }
                    break;
                case 'Set as Default': {
                    const message = 'Set "' + node.data.name + '" as the default?';
                    if (node.data instanceof Role) {
                        this.commonService.openConfirmationModal(message, 'Set as Default', 'OK', 'Cancel').then(result => {
                            if (result.modalResult) { // OK was clicked
                                node.isLoading = true;
                                const request = {
                                    organizationName: this.currentOrganization,
                                    roleHandle: node.data.handle,
                                    systemScope: this.isSystemSettingsModalOpen
                                };
                                this.restService.post(Endpoint.orgManager_setOrganizationDefaultRole, request).then(response => {
                                    node.isLoading = false;
                                    if (response && response.status === 'SUCCESS') {
                                        this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.TEMPLATE_MEMEBERSHIP);
                                    }
                                }, error => {
                                    node.isLoading = false;
                                });
                            }
                        });
                    } else {
                        this.commonService.openConfirmationModal(message, 'Set as Default', 'OK', 'Cancel').then(result => {
                            if (result.modalResult) { // OK was clicked
                                node.isLoading = true;
                                let request = {
                                    name: node.data.name, handle: node.data.handle,
                                    type: node.data.templateType, description: node.data.description, defaultTemplate: true,
                                    systemScope: this.isSystemSettingsModalOpen
                                };
                                let endpoint = Endpoint.orgManager_updateTemplate;
                                if (node.data.setAsDefaultRequestInfo) {
                                    const requestInfo = node.data.setAsDefaultRequestInfo() as [Endpoint, any];
                                    endpoint = requestInfo[0];
                                    request = requestInfo[1];
                                    Object.assign(request, { systemScope: this.isSystemSettingsModalOpen });
                                }
                                this.restService.post(endpoint, request).then(() => {
                                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.TEMPLATE_MEMEBERSHIP);
                                    node.isLoading = false;
                                }, () => {
                                    node.isLoading = false;
                                });
                            }
                        });
                    }
                }
                    break;
                case 'Save to Template ...':
                case 'Load from Template ...': {
                    this.launchTemplateModal(node, action);
                    break;
                }
            }
        }
    }

    exportTemplates(handle: string, node: GenericTreeNode) {
        this.restService.post(Endpoint.templateManager_exportTemplates, {
            handles: [handle],
            templateType: node.bundle && this.settingsType === SettingsType.project ? node.bundle : node.data.templateType,
            allTemplates: false,
            projectHandle: this.settingsType === SettingsType.project ? this.currentProject?.handle : null
        }).then(response => {
            if (response?.fileUrl) {
                this.commonService.unzipFile(response.fileUrl, this.downloadLink.nativeElement, null, null, null, true);
            }
        });
    }

    exportAllTemplates(allHandles: Array<string>, node: GenericTreeNode) {
        this.restService.post(Endpoint.templateManager_exportTemplates, {
            handles: allHandles,
            templateType: node.requestInfo.responseName === 'profiles' ? TemplateType.EXPORT_FIELDS : node.requestInfo.data.tempType,
            allTemplates: true,
            projectHandle: this.settingsType === SettingsType.project ? this.currentProject.handle : null
        }).then(response => {
            if (response?.fileUrl) {
                this.commonService.unzipFile(response.fileUrl, this.downloadLink.nativeElement, null);
            }
        });
    }

    launchTemplateModal(node: GenericTreeNode, action: 'Save to Template ...' | 'Load from Template ...'): void {
        const currentOperation = action.startsWith('Save') ? TemplateOperationType.saveAs : TemplateOperationType.loadFrom;
        const currentOrg = (this.settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser?.importedFromSystemUser)) ? 'super_system_customer' : this.currentOrganization;
        this.commonService.openModal(SaveAsLoadFromTemplateComponent, {
            class: 'vertical-percent-80',
            modalData: {
                operationType: currentOperation, selectedItem: node.data,
                templateType: node.bundle, permissions: node.permissions,
                isSystemContext: this.settingsType === SettingsType.system,
                currentOrg: currentOrg
            }
        });
    }

    closeModal(): void {
        this.checkCurrentNavigationComponent().then(result => {
            if (result) {
                this.isSystemSettingsModalOpen = false;
                super.closeModal();
            }
        });
    }

    discardChanges(): void {
        this.closeModal();
    }

    setSettingsType(value: string): void {
        this.checkCurrentNavigationComponent().then(result => {
            if (result) {
                this.isSystemSettingsModalOpen = value === SettingsType.system && this.showSystemSettings;
                this.performSettingsChange(value);
            }
        });
        this.getCustomMetadataFields(value);
    }

    performSettingsChange(value: string): void {
        this.navigateTo('settings-empty');
        this.settingsType = value as SettingsType;

        this.currentOrganization = this.commonService.currentUser?.originalCustomerName === 'super_system_customer' && this.settingsType === SettingsType.system ? 'super_system_customer' :
            this.commonService?.currentUser?.customerName || this.modalData?.organizationName || this.modalData?.orgName;
        this.restService.post(Endpoint.realmManager_initializeOrganization, {
            organizationName:
                this.commonService.currentUser?.originalCustomerName === 'super_system_customer' && this.settingsType === SettingsType.system ? 'super_system_customer' :
                    this.commonService?.currentUser?.customerName || this.modalData?.organizationName || this.modalData?.orgName,
            systemScope: this.isSystemSettingsModalOpen
        });

        this.settingsTree.setData(this.settingsInfoByType.get(this.settingsType).nodes);
        this.selectedSetting = null;
        this.selectedSettingChild = null;
        this.selectedSettingNodes = null;
        if (this.settingsTree) {
            this.settingsTree.selectedNode = null;
        }
        this.setHelpIndex();
    }

    setHelpIndex(): void {
        this.modalHelpIndex = this.settingsInfoByType.get(this.settingsType).helpIdx;
    }
    onRouterOutletActivate(component: SettingsNavigationContentComponent): void {
        if (component && component.selectedTreeItemChanged) {
            const node = this.selectedSettingChild ? this.selectedSettingChild : this.selectedSetting;
            if (node) {
                this.currentNavigationComponent = component;
                const handle = this.settingsType === SettingsType.project ? this.currentProject.handle : null;
                this.currentNavigationComponent.selectedTreeItemChanged(node, handle, this.settingsType);
            }
        }
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
        this.clearRouteOutletAndNavigate('settings-empty');
        if (this.settingsType === SettingsType.system && this.commonService.currentProject) {
            this.restService.post(Endpoint.realmManager_initializeOrganization, {
                organizationName: this.commonService?.currentUser?.customerName || this.modalData?.organizationName || this.modalData?.orgName,
                systemScope: this.isSystemSettingsModalOpen
            });
        }
        this.commonService.keepGridSpinner = false;
    }

    checkCurrentNavigationComponent(): Promise<boolean> {
        this.commonService.keepGridSpinner = false;
        return new Promise<boolean>((resolve) => {
            if (this.currentNavigationComponent && this.currentNavigationComponent.isDirty && this.currentNavigationComponent.saveChanges) {
                if (this.currentNavigationComponent.validChanges) {
                    let disableSaveButton = false;
                    if (this.currentNavigationComponent instanceof MetadataFieldsComponent &&
                        this.currentNavigationComponent.type === 'DOCUMENT_METADATA' && this.currentNavigationComponent.itemsRight && this.currentNavigationComponent.itemsRight.length === 0) {
                        disableSaveButton = true;
                    }
                    this.commonService.openConfirmationModal('There are unsaved changes.',
                        'Unsaved Changes', 'Save Changes', 'Cancel', 'Discard Changes', 'unsaved-changes-message', disableSaveButton).then(result => {
                            if (result.modalResult) {
                                if (result.modalData === 'Save Changes') {
                                    const savePromise = this.currentNavigationComponent.saveChanges(true);
                                    if (savePromise) {
                                        savePromise.then(saveResult => {
                                            resolve(saveResult);
                                        });
                                    } else {
                                        resolve(true);
                                    }
                                } else {
                                    resolve(true);
                                }
                            } else {
                                resolve(false);
                            }
                        });
                } else {
                    this.commonService.openDiscardConfirmationModal('There are invalid, unsaved changes.',
                        'Unsaved Changes', 'Discard Changes', 'Cancel', 'unsaved-changes-message').then(result => {
                            if (result.modalResult) {
                                resolve(true);
                            } else {
                                resolve(false);
                            }
                        });
                }
            } else {
                resolve(true);
            }
        });
    }

    getCustomMetadataFields(settingsType: string): void {
        if (settingsType === SettingsType.project) {
            this.metadataService.getCustomMetadataFields(settingsType, this.modalData instanceof ProjectListItem ? this.modalData?.handle : this.modalData?.projectHandle, null);
        } else if (settingsType === SettingsType.organization) {
            this.metadataService.getCustomMetadataFields(settingsType, null, this.modalData?.orgName ?? this.modalData?.organizationName);
        }
    }
}
